// External imports
import { FolderTree, FileCode, Package } from "lucide-react";

/**
 * ProjectStructureSection Component
 * Displays the project's directory structure and organization
 * Includes main structure, frontend, and backend architecture
 */
export const ProjectStructureSection = () => {
  return (
    <section id="project-structure" className="mb-8 sm:mb-12">
      {/* Section Header */}
      <h2 className="mb-3 text-xl font-bold text-neutral-950 sm:mb-4 sm:text-2xl">
        项目结构
      </h2>

      {/* Project Overview Description */}
      <div className="mb-4 sm:mb-6">
        <p className="text-sm text-neutral-600 sm:text-base">
          本项目采用 PNPM 工作区的单仓库结构，将代码组织为三个主要包：
        </p>
      </div>

      {/* Main Project Structure Section */}
      <div className="mb-6 rounded-lg border border-neutral-100 bg-white p-4 sm:mb-8 sm:p-6">
        <div className="mb-3 flex items-center gap-2 sm:mb-4">
          <FolderTree className="h-4 w-4 flex-shrink-0 text-neutral-600 sm:h-5 sm:w-5" />
          <h3 className="text-base font-semibold text-neutral-950 sm:text-lg">
            主结构
          </h3>
        </div>
        <pre className="scrollbar-thin scrollbar-thumb-neutral-300 scrollbar-track-neutral-100 overflow-x-auto rounded-lg bg-neutral-50 p-3 text-xs text-neutral-600 sm:p-4 sm:text-sm">
          {`project-root/
├── client/           # Vite React 前端应用
├── server/           # 后端 API（Hono.js、Drizzle ORM、NeonDB）
└── package.json      # 根工作区配置`}
        </pre>
      </div>

      {/* Frontend Architecture Section */}
      <div className="mb-6 rounded-lg border border-neutral-100 bg-white p-4 sm:mb-8 sm:p-6">
        <div className="mb-3 flex items-center gap-2 sm:mb-4">
          <FileCode className="h-4 w-4 flex-shrink-0 text-neutral-600 sm:h-5 sm:w-5" />
          <h3 className="text-base font-semibold text-neutral-950 sm:text-lg">
            前端结构
          </h3>
        </div>
        <pre className="scrollbar-thin scrollbar-thumb-neutral-300 scrollbar-track-neutral-100 overflow-x-auto rounded-lg bg-neutral-50 p-3 text-xs text-neutral-600 sm:p-4 sm:text-sm">
          {`client/
├── src/
│   ├── components/   # 可复用的 UI 组件
│   ├── features/     # 按功能模块组织
│   │   ├── auth/     # 认证
│   │   ├── dashboard/# 仪表盘功能
│   │   └── public/   # 公共页面
│   ├── lib/         # 工具与配置
│   ├── providers/   # 上下文提供者
│   └── routes/      # TanStack 路由配置
│   	│   __root.tsx
│       │   ├───(app)
│       │   ├───(auth)
│       │   └───(public)
└── package.json`}
        </pre>
      </div>

      {/* Backend Architecture Section */}
      <div className="mb-6 rounded-lg border border-neutral-100 bg-white p-4 sm:mb-8 sm:p-6">
        <div className="mb-3 flex items-center gap-2 sm:mb-4">
          <Package className="h-4 w-4 flex-shrink-0 text-neutral-600 sm:h-5 sm:w-5" />
          <h3 className="text-base font-semibold text-neutral-950 sm:text-lg">
            后端结构
          </h3>
        </div>
        <pre className="scrollbar-thin scrollbar-thumb-neutral-300 scrollbar-track-neutral-100 overflow-x-auto rounded-lg bg-neutral-50 p-3 text-xs text-neutral-600 sm:p-4 sm:text-sm">
          {`server/
├── src/
│   ├── database/    # 数据库配置与表结构
│   ├── routes/      # API 路由处理
│   ├── services/    # 业务逻辑
│   ├── middleware/  # 中间件
│   └── types/       # 类型定义
└── package.json`}
        </pre>
      </div>

      {/* Key Features Summary Section */}
      <div className="rounded-lg border border-amber-100 bg-amber-50 p-3 sm:p-4">
        <h4 className="mb-2 text-sm font-medium text-amber-900 sm:text-base">
          关键组织特性
        </h4>
        <ul className="list-inside list-disc space-y-1 text-xs text-amber-700 sm:text-sm">
          <li>按功能模块组织，具备良好可扩展性</li>
          <li>前后端关注点清晰分离</li>
          <li>模块化组件架构</li>
          <li>结构化的路由体系</li>
        </ul>
      </div>
    </section>
  );
};
